import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Megaphone, AlertCircle } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { getEnvironment } from '../../../utils/runtimeEnvironment';
import { getSocialApiBaseUrl, getSocialReturnUrl } from '../../../utils/socialApi';
import LoadingSpinner from '../../common/LoadingSpinner';
import {
  gymDarkContentPadding,
  gymDarkHeaderBar,
  gymSocialAlertError,
  gymSocialAlertWarn,
  gymSocialCard,
  gymSocialPageBg,
  gymSocialMutedText,
  gymSocialTabActive,
  gymSocialTabInactive,
} from '../gymPageTheme';
import GymSocialAccountPanel from './GymSocialAccountPanel';
import GymSocialPostComposer from './GymSocialPostComposer';
import GymSocialCalendar from './GymSocialCalendar';
import GymSocialPostsList from './GymSocialPostsList';

const TABS = ['posts', 'calendar', 'account'];

export default function GymSocialPage() {
  const { t } = useTranslation();
  const { getAuthenticatedApi, currentUser, loading: authLoading } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState('posts');
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);
  const [oauthError, setOauthError] = useState(null);
  const [postsRefresh, setPostsRefresh] = useState(0);
  const [composerSchedule, setComposerSchedule] = useState('');
  const [editingPost, setEditingPost] = useState(null);

  const socialApi = getSocialApiBaseUrl();
  const api = getAuthenticatedApi();

  const fetchStatus = useCallback(async () => {
    if (!currentUser) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('gym_social.php?action=status', { baseURL: socialApi });
      setStatus(res.data);
    } catch (err) {
      if (err.response?.status === 401) {
        setError(t('gym.social.tokenTestOnly'));
      } else {
        const msg = err.response?.data?.error || err.message || t('gym.social.loadError');
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  }, [socialApi, currentUser, api, t]);

  useEffect(() => {
    if (!authLoading) {
      fetchStatus();
    }
  }, [fetchStatus, authLoading]);

  useEffect(() => {
    const connected = searchParams.get('connected');
    const oauthErrParam = searchParams.get('error');
    const igWarning = searchParams.get('ig_warning');
    if (connected === '1') {
      setOauthError(null);
      const connectMode = searchParams.get('connect_mode');
      if (igWarning === '1') {
        toast(t('gym.social.connectPartialIg'), { icon: '⚠️', duration: 10000 });
      } else if (connectMode === 'ig_only') {
        toast.success(t('gym.social.connectSuccessIgOnly'), { duration: 10000 });
      } else {
        toast.success(t('gym.social.connectSuccess'));
      }
      setSearchParams({}, { replace: true });
      fetchStatus();
    } else if (connected === '0' && oauthErrParam) {
      const msg = decodeURIComponent(oauthErrParam);
      setOauthError(msg);
      toast.error(msg, { duration: 8000 });
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams, fetchStatus, t]);

  const bumpPosts = () => setPostsRefresh((k) => k + 1);

  const handleConnectInstagram = async () => {
    if (!currentUser) return;
    setConnecting(true);
    try {
      const res = await api.post(
        'gym_social.php?action=instagram_oauth_start',
        { return_url: getSocialReturnUrl() },
        { baseURL: socialApi }
      );
      if (res.data?.oauth_url) {
        window.location.href = res.data.oauth_url;
        return;
      }
      toast.error(t('gym.social.connectError'));
    } catch (err) {
      toast.error(err.response?.data?.error || t('gym.social.connectError'));
    } finally {
      setConnecting(false);
    }
  };

  const handleConnect = async () => {
    if (!currentUser) return;
    setConnecting(true);
    try {
      const res = await api.post(
        'gym_social.php?action=oauth_start',
        { return_url: getSocialReturnUrl() },
        { baseURL: socialApi }
      );
      if (res.data?.oauth_url) {
        window.location.href = res.data.oauth_url;
        return;
      }
      toast.error(t('gym.social.connectError'));
    } catch (err) {
      toast.error(err.response?.data?.error || t('gym.social.connectError'));
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!currentUser || !window.confirm(t('gym.social.disconnectConfirm'))) return;
    try {
      await api.post('gym_social.php?action=disconnect', {}, { baseURL: socialApi });
      toast.success(t('gym.social.disconnectSuccess'));
      fetchStatus();
      bumpPosts();
    } catch (err) {
      toast.error(err.response?.data?.error || t('gym.social.disconnectError'));
    }
  };

  if (authLoading || (loading && !status)) {
    return (
      <div className={`flex min-h-[40vh] w-full items-center justify-center ${gymSocialPageBg}`}>
        <LoadingSpinner />
      </div>
    );
  }

  const showDevHint = getEnvironment() === 'dev';
  const canPost = status?.connected;

  return (
    <div className={`w-full max-w-full min-h-full ${gymSocialPageBg}`}>
      <header className={`${gymDarkHeaderBar} px-fluid-lg py-fluid-md sm:px-fluid-xl`}>
        <div className="flex items-center gap-3">
          <Megaphone className="h-8 w-8 text-white shrink-0" />
          <div className="min-w-0">
            <h1 className="text-2xl font-bold text-white truncate">{t('gym.social.title')}</h1>
            <p className="text-sm text-white/80 mt-0.5">{t('gym.social.subtitle')}</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 mt-4">
          {TABS.map((id) => (
            <button
              key={id}
              type="button"
              onClick={() => setTab(id)}
              className={tab === id ? gymSocialTabActive : gymSocialTabInactive}
            >
              {t(`gym.social.tabs.${id}`)}
            </button>
          ))}
        </div>
      </header>

      <div className={`${gymDarkContentPadding} w-full max-w-full space-y-6`}>
        {showDevHint && (
          <div className={gymSocialAlertWarn}>{t('gym.social.devApiHint')}</div>
        )}

        {(oauthError || error) && (
          <div className={`flex gap-2 ${gymSocialAlertError}`}>
            <AlertCircle className="h-5 w-5 shrink-0" />
            <span>{oauthError || error}</span>
          </div>
        )}

        {tab === 'account' && (
          <>
            {status && !status.meta_configured && (
              <div
                className={`${gymSocialCard} px-4 py-3 text-sm text-gray-600 dark:text-white/75`}
              >
                {t('gym.social.serverNotConfigured')}
              </div>
            )}
            <GymSocialAccountPanel
              status={status}
              connecting={connecting}
              onConnectMeta={handleConnect}
              onConnectInstagram={handleConnectInstagram}
              onDisconnect={handleDisconnect}
            />
          </>
        )}

        {tab === 'posts' && (
          <>
            {!canPost ? (
              <div className={gymSocialAlertWarn}>{t('gym.social.posts.connectFirst')}</div>
            ) : (
              <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 w-full">
                <div className="xl:col-span-2 min-w-0">
                  <GymSocialPostComposer
                    api={api}
                    socialLinks={{
                      facebook: !!status?.fb_linked,
                      instagram: !!status?.ig_linked,
                    }}
                    initialScheduledAt={composerSchedule}
                    editingPost={editingPost}
                    onCancelEdit={() => setEditingPost(null)}
                    onSaved={() => {
                      bumpPosts();
                      setComposerSchedule('');
                      setEditingPost(null);
                    }}
                  />
                </div>
                <div className={`${gymSocialCard} p-6 min-w-0`}>
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                    {t('gym.social.posts.recent')}
                  </h3>
                  <p className={`text-xs mb-3 ${gymSocialMutedText}`}>
                    {t('gym.social.posts.listAutoRefresh')}
                  </p>
                  <GymSocialPostsList
                    api={api}
                    refreshKey={postsRefresh}
                    onChanged={() => setPostsRefresh((k) => k + 1)}
                    onEditPost={(post) => {
                      setEditingPost(post);
                      setComposerSchedule('');
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                  />
                </div>
              </div>
            )}
          </>
        )}

        {tab === 'calendar' && (
          <>
            {!canPost ? (
              <div className={gymSocialAlertWarn}>{t('gym.social.posts.connectFirst')}</div>
            ) : (
              <GymSocialCalendar
                api={api}
                refreshKey={postsRefresh}
                onChanged={bumpPosts}
                onCreateForDay={(dt) => {
                  setComposerSchedule(dt);
                  setTab('posts');
                }}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}
